import { cn } from "@gettactic/helpers";
import { AlertCircle, AlertTriangle, ChevronDown } from "lucide-react";
import type React from "react";

export interface ImportNotesProps {
  items: string[];
  tone?: "warning" | "error";
  /** One-line summary shown while collapsed, e.g. "3 notes from the reconstruction". */
  title: string;
}

/**
 * Model notes and part errors, collapsed to one summary line so they don't
 * push the map preview down while it builds. Open to read the details.
 */
export const ImportNotes: React.FC<ImportNotesProps> = ({
  items,
  tone = "warning",
  title
}) => {
  const unique = [...new Set(items)];
  if (!unique.length) return null;
  const Icon = tone === "error" ? AlertCircle : AlertTriangle;
  return (
    <details
      className={cn(
        "group rounded-xl border px-4 py-2.5 text-sm",
        tone === "error"
          ? "border-red-200 bg-red-50/70"
          : "border-amber-200 bg-amber-50/60"
      )}
    >
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center gap-2 font-medium [&::-webkit-details-marker]:hidden",
          tone === "error" ? "text-red-800" : "text-amber-900"
        )}
      >
        <Icon size={16} className="shrink-0" />
        <span className="min-w-0 flex-1">{title}</span>
        <span className="font-normal text-neutral-500 text-xs group-open:hidden">
          Show
        </span>
        <ChevronDown
          size={16}
          className="shrink-0 text-neutral-500 transition-transform group-open:rotate-180 motion-reduce:transition-none"
        />
      </summary>
      <ul
        className={cn(
          "mt-2 max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-xs leading-relaxed",
          tone === "error" ? "text-red-800" : "text-amber-900"
        )}
      >
        {unique.map((item) => (
          <li key={item} className="whitespace-pre-wrap break-words">
            {item}
          </li>
        ))}
      </ul>
    </details>
  );
};
